import { addIsNewFieldToUsers, searchProperty } from 'client/store/mixins/ImportUsersMixin';
import CollectionModule from 'store/modules/CollectionModule';
import hashString from 'client/GrowthEngineering/utilities/hashString';

// Helper to add an Id to users being imported without one
const getId = (user) => user.id || hashString(user.email);
export class DirectoryImportCollectionClass extends CollectionModule {
  // ----------------------------------------------------------------------------------------------
  // Actions
  // ----------------------------------------------------------------------------------------------
  /**
   * Override method from ImportUsersMixin since the M365/Google ImportApi must be
   * called to fetch users from M365/Google and handle filtering of computed field.
   * @todo Pass M365/Google ID to API instance if necessary.
   */
  async fetchCollection(context, { next = false } = {}) {
    const { limit, skipToken, nextPageToken } = context.state.features.items.pagination;
    const { filters, searchTerm, sort } = context.state.features.items;

    // Detect if all results have been fetched (pagination token set to null)
    if (next === true && !skipToken && !nextPageToken) {
      return null;
    }

    try {
      let items = await this.fetchWrapper(context, 0, limit, sort, searchTerm,
        filters, nextPageToken, skipToken);
      let o365User;
      let gSuiteUser;
      let identifierProperty;
      if (Array.isArray(items.results) && items.results.length) {
        // Get property to see what type of user is
        o365User = items.results[0].userPrincipalName;
        gSuiteUser = items.results[0].primaryEmail || items.results[0].email;
        // Since those systemusers are not created yet they don't have ID.
        // hashString function generates a numeric code from a string (email address)
        // to be used as an id in the itemtable component.
        if (gSuiteUser && !items.results[0].id) {
          items.results = items.results.map((user) => ({ ...user, id: getId(user) }));
        }
        // Add computed isNew field
        identifierProperty = searchProperty(items.results[0]);
        items.results = await addIsNewFieldToUsers(items.results, identifierProperty);
        // Filter computed isNew field manually
        if (filters && filters.some(filter => filter.name === 'isNew')) {
          const options = { filters, searchTerm, sort };
          items = await this.filterNewUsers(
            context, items.results, items.nextPageToken, items.skipToken, options,
          );
        }
      }

      let payload = { totalCount: 0, results: [] };
      if (gSuiteUser && items.results.length > 0) {
        payload = {
          results: items.results,
          totalCount: items.totalCount,
          totalCountOfAllItems: items.totalCount,
          nextPageToken: items.nextPageToken,
        };
      } else if (o365User && items.results.length > 0) {
        payload = {
          results: items.results,
          totalCount: items.totalCount,
          totalCountOfAllItems: items.totalCount,
          skipToken: items.skipToken,
        };
      }

      if (context.state.entities.items.totalCountOfAllItems === null) {
        payload.totalCountOfAllItems = payload.totalCount;
      }

      context.commit('SET_COLLECTION', payload);

      return items.results;
    } catch (err) {
      if (err && err.status && err.status < 500) {
        context.commit('SET_IMPORT_ERROR', err.message);
        return [];
      }

      return Promise.reject(err);
    }
  }

  /**
   * Helper for fetchCollection. Filter users by isNew = true. If enough users
   * get filtered out to mess up the scroll experience, fetch another batch.
   *
   * @param {Array} users - users to filter
   * @param {String} nextPageToken - token for next page of results
   * @param {String} skipToken - token for next page of results
   * @param {Object} options - searchTerm or sort to apply
   */
  async filterNewUsers(context, users, nextPageToken, skipToken, options = {}) {
    const filteredUsers = users.filter(user => user.isNew);

    // Keep fetching/ filtering results until we have significant amount to show
    // or reach end of results
    if (filteredUsers.length < 10 && (nextPageToken || skipToken)) {
      const { filters, searchTerm, sort } = options;
      const items = await this.fetchWrapper(context, 0, 50, sort, searchTerm, filters,
        nextPageToken, skipToken);

      const identifierProperty = searchProperty(items.results[0]);
      items.results = await addIsNewFieldToUsers(items.results, identifierProperty);
      filteredUsers.push(...items.results);
      return this.filterNewUsers(context, filteredUsers, items.nextPageToken,
        items.skipToken, options);
    }
    if (skipToken) {
      return {
        results: filteredUsers,
        skipToken,
        totalCount: filteredUsers.length,
      };
    }
    return { results: filteredUsers, nextPageToken, totalCount: filteredUsers.length };
  }

  /**
   * Override method from CollectionModule
   * We need to account for isNew filter being applied so the totalCount displayed is accurate
   */
  async selectAllItems(context) {
    const { features } = context.state;
    const { filters, searchTerm } = features.items;
    const users = await this.Api.fetchAll(searchTerm);
    let usersWithIsNew;

    const identifierProperty = searchProperty(users[0]);

    if (identifierProperty) {
      usersWithIsNew = await addIsNewFieldToUsers(users, identifierProperty);
    }
    let items;
    if (filters && filters.some(filter => filter.name === 'isNew')) {
      items = usersWithIsNew.filter(user => user.isNew);
    } else {
      items = usersWithIsNew;
    }

    const ids = [];
    const parsedItems = items.map((item) => {
      const id = getId(item);
      ids.push(id);
      return { ...item, id };
    });

    context.commit('SELECT_ITEMS', { ids });
    context.commit('ADD_BOUND_ITEMS', parsedItems);
  }
}

export default new DirectoryImportCollectionClass().extractModule();
